"use client";

import { apijs } from "@metrojs/client";
import logjs from "@metrojs/logjs";
import { useCustomDialog } from "../../../hooks";
import SearchPartsDialog from "./SearchPartsDialog";

const log = new logjs("useSearchPartsDialog");
const api = new apijs("api/SearchPartsDialog");

/**
 * 部品選択ダイアログの開閉フック
 * 2026-09-11 tdaig seller 受注一覧 No.56
 * 2026-09-15 tdaig packages/common へ移設（buyer / seller 共通）
 * @returns {{ open, onClose, isOpen, renderDialog, renderDialogEx }}
 */
export const useSearchPartsDialog = () => {
  log.debug("useSearchPartsDialog");
  const { open, onClose, isOpen, renderDialog, renderDialogEx } = useCustomDialog(SearchPartsDialog, { resetOnClose: true });
  return { open, onClose, isOpen, renderDialog, renderDialogEx };
};

/**
 * ダイアログ内の API 操作
 * @param {Object} actions - showLoading / showError 等
 * @returns {Object} API 関数
 */
export function useSearchPartsDialogApi(actions = {}) {
  /**
   * 大分類を取得する
   * @returns {Promise<Object>} 初期データ
   */
  async function init() {
    log.debug("START init");
    try {
      const result = await api.post({ mode: "init" });
      if (result?.error || result?.result === false) {
        throw new Error(result.error || "初期データの取得に失敗しました");
      }
      return result;
    } catch (error) {
      log.error("init error", error);
      if (actions.showError) {
        actions.showError(error.message || "初期データの取得に失敗しました");
      }
      throw error;
    }
  }

  /**
   * 中分類を取得する
   * @param {string|number} daiBunruiCD - 大分類コード
   * @returns {Promise<Array>} 中分類リスト
   */
  async function getChuBunrui(daiBunruiCD) {
    log.debug("START getChuBunrui", daiBunruiCD);
    try {
      const result = await api.post({ mode: "getChuBunrui", daiBunruiCD });
      if (result?.error || result?.result === false) {
        throw new Error(result.error || "中分類の取得に失敗しました");
      }
      return Array.isArray(result.chuBunruiList) ? result.chuBunruiList : [];
    } catch (error) {
      log.error("getChuBunrui error", error);
      if (actions.showError) {
        actions.showError(error.message || "中分類の取得に失敗しました");
      }
      throw error;
    }
  }

  /**
   * 部品を検索する
   * @param {Object} params - 検索条件
   * @returns {Promise<Object>} 検索結果
   */
  async function search(params) {
    log.debug("START search", params);
    try {
      const result = await api.post({ mode: "search", ...params });
      if (result?.error || result?.result === false) {
        throw new Error(result.error || "部品の検索に失敗しました");
      }
      return {
        rows: Array.isArray(result.rows) ? result.rows : [],
        total: Number(result.total) || 0,
        page: Number(result.page) || 1,
        pageSize: Number(result.pageSize) || 10,
      };
    } catch (error) {
      log.error("search error", error);
      if (actions.showError) {
        actions.showError(error.message || "部品の検索に失敗しました");
      }
      throw error;
    }
  }

  /**
   * 選択部品を保存する
   * @param {Array} parts - 選択部品
   * @returns {Promise<Array>} 保存後の部品
   */
  async function save(parts) {
    log.debug("START save", { count: parts?.length });
    try {
      if (actions.showLoading) {
        actions.showLoading();
      }
      const result = await api.post({ mode: "save", parts: parts || [] });
      if (result?.error || result?.result === false) {
        throw new Error(result.error || "部品選択条件の保存に失敗しました");
      }
      return Array.isArray(result.parts) ? result.parts : [];
    } catch (error) {
      log.error("save error", error);
      if (actions.showError) {
        actions.showError(error.message || "部品選択条件の保存に失敗しました");
      }
      throw error;
    } finally {
      if (actions.hideLoading) {
        actions.hideLoading();
      }
    }
  }

  return { init, getChuBunrui, search, save };
}

export default useSearchPartsDialog;
